// Real-time cart page sync.
//
// Replaces the standard "edit qty → click Update Cart → full page reload"
// flow on /cart/ with debounced AJAX calls so the line subtotal, totals
// card (subtotal / VAT / shipping / total), the page header count, and the
// header bubble all update in place.
//
// Wires three controls per row:
//   • Quantity input (.qty / input[type=number]) → debounced update on `input`
//   • Plus/minus stepper buttons → immediate update on click
//   • Remove (.remove) link → preventDefault + AJAX, removes the row when done
//
// All requests go through admin-ajax.php?action=haegoziya_cart_update with
// the haegoziya_qty nonce that's already localised on every page (see
// inc/enqueue.php → wp_localize_script).
//
// Failure UX: if the server rejects the change (out of stock, etc.) the
// input snaps back to the previous value and a small inline error appears
// briefly under the row.

(function () {
    function init() {
        const cart = document.querySelector('.haegoziya-cart');
        if (!cart) return; // Only runs on the cart page

        cart.querySelectorAll('[data-cart-row]').forEach(setupRow);

        // Hide Woo's "Update cart" button — the AJAX flow makes it pointless.
        // Keeping it would let users click it and trigger a full-page reload
        // that contradicts the live updates they just made.
        cart.querySelectorAll('button[name="update_cart"]').forEach((btn) => {
            btn.style.display = 'none';
        });
    }

    function setupRow(row) {
        const key = row.dataset.cartKey;
        if (!key) return;

        const qtyInput = row.querySelector('input.qty, input[type="number"][name^="cart["]');
        const removeLink = row.querySelector('a.remove');
        const subtotalCell = row.querySelector('[data-cart-line-subtotal]');

        // Plus/minus buttons (Woo's quantity input ships with these when
        // wc-quantity-buttons-style is enabled; if not, the input alone is fine).
        const plusBtn  = row.querySelector('.plus, [data-qty-inc]');
        const minusBtn = row.querySelector('.minus, [data-qty-dec]');

        if (qtyInput) {
            // Debounce so a user typing "12" doesn't fire an update on "1".
            let debounceTimer;
            qtyInput.addEventListener('input', () => {
                clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => commit(qtyInput, key, row, subtotalCell), 450);
            });
            qtyInput.addEventListener('change', () => {
                clearTimeout(debounceTimer);
                commit(qtyInput, key, row, subtotalCell);
            });
        }

        if (plusBtn && qtyInput) {
            plusBtn.addEventListener('click', () => {
                qtyInput.value = String(parseInt(qtyInput.value, 10) + 1);
                commit(qtyInput, key, row, subtotalCell);
            });
        }
        if (minusBtn && qtyInput) {
            minusBtn.addEventListener('click', () => {
                const next = Math.max(0, parseInt(qtyInput.value, 10) - 1);
                qtyInput.value = String(next);
                commit(qtyInput, key, row, subtotalCell);
            });
        }

        if (removeLink) {
            removeLink.addEventListener('click', (e) => {
                e.preventDefault();
                if (qtyInput) qtyInput.value = '0';
                commit(qtyInput, key, row, subtotalCell);
            });
        }
    }

    let pendingByKey = new Map();
    let lastValueByKey = new Map();

    async function commit(qtyInput, key, row, subtotalCell) {
        const cfg = window.HAEGOZIYA || {};
        const nonce = cfg.qtyNonce || '';
        if (!nonce) return;

        const quantity = qtyInput
            ? Math.max(0, parseInt(qtyInput.value, 10) || 0)
            : 0;

        // De-dupe — if a request for this row is in flight, the next change
        // will fire after it returns (see the .finally block).
        if (pendingByKey.get(key)) {
            pendingByKey.set(key + ':queued', quantity);
            return;
        }
        pendingByKey.set(key, true);

        const prevValue = lastValueByKey.get(key);
        if (qtyInput) lastValueByKey.set(key, qtyInput.value);

        row.classList.add('is-updating');

        const body = new FormData();
        body.append('action',   'haegoziya_cart_update');
        body.append('nonce',    nonce);
        body.append('key',      key);
        body.append('quantity', String(quantity));

        try {
            const res = await fetch(cfg.ajaxUrl || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                credentials: 'same-origin',
                cache: 'no-store',
                body,
            });
            const json = await res.json().catch(() => null);
            if (!res.ok || !json || !json.success) {
                throw new Error((json && json.data && json.data.message) || 'Cart update failed');
            }

            const data = json.data;

            if (data.removed) {
                row.classList.add('is-removed');
                // Slight fade-out then remove from DOM
                setTimeout(() => row.remove(), 220);
            } else {
                if (subtotalCell && data.line_subtotal) {
                    subtotalCell.innerHTML = data.line_subtotal;
                }
                if (qtyInput && data.line_quantity != null) {
                    qtyInput.value = String(data.line_quantity);
                }
            }

            // Page header count ("X items")
            const pageCount = document.querySelector('[data-cart-page-count]');
            if (pageCount && data.total_qty != null) {
                const n = data.total_qty;
                pageCount.textContent = (n === 1 ? '1 item' : `${n} items`);
            }

            // Totals card (subtotal / VAT / shipping / total)
            const totalsWrap = document.querySelector('[data-cart-totals-wrap]');
            if (totalsWrap && data.totals_html) {
                // Replace only the .cart_totals block, leave cross-sells
                // (or other collaterals) intact if they exist.
                const existing = totalsWrap.querySelector('.cart_totals');
                if (existing) {
                    const tmp = document.createElement('div');
                    tmp.innerHTML = data.totals_html;
                    const fresh = tmp.querySelector('.cart_totals') || tmp.firstElementChild;
                    if (fresh) existing.replaceWith(fresh);
                } else {
                    totalsWrap.innerHTML = data.totals_html + totalsWrap.innerHTML;
                }
            }

            // Header bubble + any other registered Woo fragments
            applyFragments(data.fragments);

            // Empty-cart state — refresh so Woo can render its empty-cart UI.
            if (data.empty) {
                window.location.reload();
                return;
            }

            // Trigger Woo's standard event so any other listener wakes up.
            if (window.jQuery) {
                window.jQuery(document.body).trigger('updated_cart_totals');
                window.jQuery(document.body).trigger('wc_fragment_refresh');
            }
        } catch (err) {
            // Roll back the input value
            if (qtyInput && prevValue != null) qtyInput.value = prevValue;
            showRowError(row, err.message || 'Could not update');
        } finally {
            row.classList.remove('is-updating');
            pendingByKey.delete(key);

            // If a value was queued during the in-flight request, fire it now
            // so the final state matches the user's last intent.
            const queued = pendingByKey.get(key + ':queued');
            if (queued != null && qtyInput) {
                pendingByKey.delete(key + ':queued');
                qtyInput.value = String(queued);
                commit(qtyInput, key, row, subtotalCell);
            }
        }
    }

    function applyFragments(fragments) {
        if (!fragments) return;
        for (const [selector, html] of Object.entries(fragments)) {
            document.querySelectorAll(selector).forEach((el) => { el.outerHTML = html; });
        }
    }

    function showRowError(row, msg) {
        // Inline error pill — auto-dismisses after 3s.
        let pill = row.querySelector('[data-cart-row-error]');
        if (!pill) {
            pill = document.createElement('div');
            pill.dataset.cartRowError = '';
            pill.className = 'cart-row-error';
            row.appendChild(pill);
        }
        pill.textContent = msg;
        pill.classList.add('is-visible');
        clearTimeout(pill._t);
        pill._t = setTimeout(() => pill.classList.remove('is-visible'), 3000);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
